// Credential detector. Every write - web, Claude, import - runs through this on
// the server, and the web app runs the same rules as people type so they see the
// problem before they submit. Pure TypeScript so it works in the browser too.
//
// Rules cover the common token formats (the ground secretlint's recommended
// preset covers) plus the ones this team is most likely to paste: Anthropic keys,
// Entra client secrets, Cloudflare tunnel tokens, database connection strings.

export interface Finding {
  rule: string;
  label: string;
  start: number;
  end: number;
  /** masked excerpt that is safe to show or log */
  preview: string;
}

interface Rule {
  id: string;
  label: string;
  re: RegExp;
  /** capture group holding the secret itself (default: whole match) */
  group?: number;
  /** return false to ignore a match (placeholders, plain words) */
  keep?: (secret: string) => boolean;
}

const PLACEHOLDER =
  /^(?:\*+|x+|X+|\.{3,}|<[^>]*>|\$\{[^}]*\}|\{\{?[^}]*\}?\}|%[A-Z_]+%|\$[A-Z_]+|password|passwd|pass|pwd|secret|changeme|redacted|example|your[-_ ]?\w*|null|none|undefined|true|false)$/i;

function notPlaceholder(s: string) {
  return !PLACEHOLDER.test(s.replace(/^["']|["']$/g, ""));
}

function entropy(s: string) {
  const counts = new Map<string, number>();
  for (const c of s) counts.set(c, (counts.get(c) ?? 0) + 1);
  let h = 0;
  for (const n of counts.values()) h -= (n / s.length) * Math.log2(n / s.length);
  return h;
}

// code that *refers* to a secret rather than containing one, e.g. a pasted
// `clientSecret: config.microsoft.clientSecret` or `password: decodeURIComponent(url.password)`
const CODE_REF = /^[A-Za-z_$][\w$]*(?:\??\.[A-Za-z_$][\w$]*)+(?:\(\))?$/;
const CODE_CALL = /^[A-Za-z_$][\w$.?]*\([\w$.?]*\)$/;
const isCode = (s: string) => [s, s.replace(/[)\]}]+$/, "")].some((v) => CODE_REF.test(v) || CODE_CALL.test(v));

/** a value after `password=` etc. that looks like a real credential, not a word in a sentence */
function looksLikeCredential(s: string) {
  if (!notPlaceholder(s) || isCode(s)) return false;
  if (/[0-9]/.test(s) || /[^A-Za-z0-9]/.test(s)) return true;
  return s.length >= 16 && entropy(s) >= 3.5;
}

const RULES: Rule[] = [
  { id: "private-key", label: "Private key", re: /-----BEGIN (?:[A-Z0-9]+ )*PRIVATE KEY(?: BLOCK)?-----/g },
  { id: "aws-access-key", label: "AWS access key", re: /\b(?:AKIA|ASIA|ABIA|ACCA)[A-Z0-9]{16}\b/g },
  {
    id: "aws-secret-key",
    label: "AWS secret key",
    re: /aws.{0,20}?(?:secret|key).{0,20}?[\s'"=:]([A-Za-z0-9/+=]{40})(?![A-Za-z0-9/+=])/gi,
    group: 1,
  },
  { id: "github-token", label: "GitHub token", re: /\b(?:gh[pousr]_[A-Za-z0-9]{36,255}|github_pat_[A-Za-z0-9_]{22,255})\b/g },
  { id: "anthropic-key", label: "Anthropic API key", re: /\bsk-ant-[A-Za-z0-9_-]{20,}/g },
  { id: "openai-key", label: "OpenAI API key", re: /\bsk-(?:proj|svcacct|admin)-[A-Za-z0-9_-]{20,}/g },
  { id: "stripe-key", label: "Stripe key", re: /\b(?:sk|rk)_(?:live|test)_[A-Za-z0-9]{16,}\b/g },
  { id: "slack-token", label: "Slack token", re: /\bxox[abposr]-[A-Za-z0-9-]{10,}/g },
  {
    id: "slack-webhook",
    label: "Slack webhook",
    re: /https:\/\/hooks\.slack\.com\/services\/T[A-Z0-9]+\/B[A-Z0-9]+\/[A-Za-z0-9]+/g,
  },
  { id: "google-api-key", label: "Google API key", re: /\bAIza[0-9A-Za-z_-]{35}\b/g },
  { id: "npm-token", label: "npm token", re: /\bnpm_[A-Za-z0-9]{36}\b/g },
  { id: "sendgrid-key", label: "SendGrid key", re: /\bSG\.[A-Za-z0-9_-]{22}\.[A-Za-z0-9_-]{43}\b/g },
  { id: "shopify-token", label: "Shopify token", re: /\bshp(?:at|ca|pa|ss)_[a-fA-F0-9]{32}\b/g },
  {
    id: "entra-client-secret",
    label: "Microsoft Entra client secret",
    re: /(?<![A-Za-z0-9_~.-])[A-Za-z0-9_~.-]{3}\dQ~[A-Za-z0-9_~.-]{31,34}(?![A-Za-z0-9_~.-])/g,
  },
  { id: "azure-storage-key", label: "Azure storage key", re: /AccountKey=([A-Za-z0-9+/=]{40,})/g, group: 1 },
  { id: "azure-sas", label: "Azure SAS signature", re: /[?&]sig=([A-Za-z0-9%+/=]{30,})/g, group: 1 },
  { id: "cloudflare-tunnel-token", label: "Cloudflare tunnel token", re: /\beyJhIjoi[A-Za-z0-9+/=_-]{50,}/g },
  { id: "jwt", label: "JWT / session token", re: /\beyJ[A-Za-z0-9_-]{8,}\.eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{10,}/g },
  {
    id: "url-credentials",
    label: "Password in a URL or connection string",
    re: /\b[a-z][a-z0-9+.-]*:\/\/[^\s:@/]+:([^\s@/]+)@[^\s/]+/gi,
    group: 1,
    keep: notPlaceholder,
  },
  {
    id: "auth-header",
    label: "Authorization header",
    re: /\b(?:Authorization\s*:\s*)?(?:Bearer|Basic)\s+([A-Za-z0-9._~+/=-]{20,})/g,
    group: 1,
    keep: (s) => looksLikeCredential(s),
  },
  {
    id: "password-assignment",
    label: "Password or secret value",
    re: /\b(?:password|passwd|pwd|passphrase|secret|client[_-]?secret|api[_-]?key|apikey|access[_-]?key|access[_-]?token|auth[_-]?token|refresh[_-]?token|private[_-]?key|token)\b["']?\s*(?:[:=]|=>|\bis\b)\s*["']?([^\s"',;<>]{6,})/gi,
    group: 1,
    keep: looksLikeCredential,
  },
];

function mask(s: string) {
  if (s.length <= 8) return "*".repeat(s.length);
  return `${s.slice(0, 4)}…${s.slice(-2)}`;
}

/** Find credentials in a piece of text. Findings are sorted and never overlap. */
export function scanText(text: string | null | undefined): Finding[] {
  if (!text) return [];
  const found: Finding[] = [];
  for (const rule of RULES) {
    rule.re.lastIndex = 0;
    for (let m = rule.re.exec(text); m; m = rule.re.exec(text)) {
      const secret = rule.group ? m[rule.group] : m[0];
      if (!secret) continue;
      if (rule.keep && !rule.keep(secret)) continue;
      const start = rule.group ? m.index + m[0].lastIndexOf(secret) : m.index;
      found.push({ rule: rule.id, label: rule.label, start, end: start + secret.length, preview: mask(secret) });
      if (m[0].length === 0) rule.re.lastIndex++;
    }
  }
  found.sort((a, b) => a.start - b.start || b.end - a.end);
  const out: Finding[] = [];
  for (const f of found) {
    const prev = out[out.length - 1];
    if (prev && f.start < prev.end) continue;
    out.push(f);
  }
  return out;
}

/** Replace every credential with a marker, e.g. "[redacted: GitHub token]". Used on text we
 *  keep or pass on (a Teams message, text sent to Claude) rather than refusing it outright. */
export function redact(text: string | null | undefined): string {
  if (!text) return text ?? "";
  let out = "";
  let at = 0;
  for (const f of scanText(text)) {
    out += `${text.slice(at, f.start)}[redacted: ${f.label}]`;
    at = f.end;
  }
  return out + text.slice(at);
}

export interface FieldFindings {
  field: string;
  findings: Finding[];
}

/** Scan several named fields at once, e.g. `{ title, body }`. */
export function scanFields(fields: Record<string, string | null | undefined>): FieldFindings[] {
  return Object.entries(fields)
    .map(([field, value]) => ({ field, findings: scanText(value) }))
    .filter((f) => f.findings.length > 0);
}

/** One-line, human description for error messages. */
export function describe(hits: FieldFindings[]): string {
  const labels = [...new Set(hits.flatMap((h) => h.findings.map((f) => f.label)))];
  return `This looks like it contains a credential (${labels.join(", ")}). Remove it before saving — put a placeholder like <token> instead.`;
}
